import {Injectable} from '@angular/core';

import {BehaviorSubject, Subject} from 'rxjs';

import * as moment from 'moment/moment';

import {ZoneValues} from '@gp/shared/form-generator/form-generator.interfaces';

@Injectable({
  providedIn: 'root'
})
export class EmployeesUtilsService {

  public employeeDeleted$: Subject<number> = new Subject<number>();

  public contractsDeleted$: Subject<number[]> = new Subject<number[]>();

  public accessCriterionsChanged$: Subject<void> = new Subject<void>();

  // Indicates whether the employee form is standalone or embedded in a gp-modal.
  // Defaults to false
  public isEmployeeFormEmbedded$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /**
   * Extracts the display text for a given zone name from a list of data records.
   * Picks the currently active dated record (where today falls between begindate and enddate),
   * falling back to the first record.
   *
   * @param zoneValues - flat array of zone data records
   * @param zoneName - the zone identifier to look up (e.g. 'Position', 'Workplace', 'OrganizationalUnit')
   * @returns the display string (transId or value) or null if not found / empty
   */
  public extractZoneValue(zoneValues: ZoneValues[], zoneName: string): string | null {
    if (!zoneValues?.length) {
      return null;
    }

    // Only consider records that actually contain the requested zone
    const relevant = zoneValues.filter(r => {
      const zoneVals = r._values || r.values;
      return zoneVals && zoneVals[zoneName] !== undefined;
    });

    if (!relevant.length) {
      return null;
    }

    const today = moment();

    // Among those, find the currently active dated record, or fall back to the first one
    const active = relevant.find(r =>
      r.begindate && r.enddate &&
      today.isBetween(moment(r.begindate), moment(r.enddate), 'day', '[]')
    ) || relevant[0];

    const vals = active?._values || active?.values;
    const field = vals[zoneName];

    if (!field) {
      return null;
    }

    const display = field.transId ?? field.value;
    if (!display || display === '') {
      return null;
    }

    return typeof display === 'string' ? display : String(display);
  }
}
